<template>
  <div class="post-caption-editor-panel">
    <textarea
      ref="captionTextareaRef"
      :class="{
        'global-textarea': true,
        'post-caption-editor-panel__textarea': true,
        'global-textarea--error': props.hasError,
      }"
      :style="{ minHeight: props.textareaMinHeight, height: props.textareaMinHeight }"
      :disabled="props.isDisabled"
      :placeholder="props.placeholder"
      v-model="caption"
      :maxlength="MAX_CAPTION_LENGTH"
      @focus="emit('focus')"
    ></textarea>

    <div class="post-caption-editor-panel__buttons-panel">
      <LinkWithIcon
        v-if="!isAICaptionEditorVisible"
        class="post-caption-editor-panel__ai-link"
        isDottedUnderline
        :color="props.aiLinkColor"
        :isDisabled="props.isDisabled || isResolvingCalendarId"
        @click="openAICaptionEditor"
      >
        <template #icon><IconEmbedded name="ai_2-5" :size="16" /></template>
        <span>Adjust with AI</span>
      </LinkWithIcon>
      <CaptionEmojiPicker :isDisabled="props.isDisabled" @select="insertEmojiAtCursor" />
      <LinkWithIcon class="post-caption-editor-panel__copy-link" isDottedUnderline color="gray" :isDisabled="props.isCopyDisabled || props.isDisabled" @click="copyCaption">
        <template #icon>
          <IconEmbedded v-if="isShowCopied" name="check_2-5" :size="16" />
          <IconEmbedded v-else name="copy_2-5" :size="16" />
        </template>
        <span>{{ isShowCopied ? "copied" : "copy" }}</span>
      </LinkWithIcon>
      <div class="post-caption-editor-panel__char-count">{{ (caption || "").length }} / {{ MAX_CAPTION_LENGTH }}</div>
      <div v-if="isCaptionAtLimit" class="post-caption-editor-panel__limit-warning">Caption limit reached</div>
    </div>

    <AICaptionEditor
      v-if="isAICaptionEditorVisible"
      :key="props.aiEditorKey ?? 'default'"
      class="post-caption-editor-panel__ai-editor"
      :customerId="props.customerId"
      :calendarId="resolvedCalendarId"
      :postId="props.postId ?? ''"
      :currentCaption="caption"
      :assetIds="props.assetIds ?? []"
      :isDisabled="props.isDisabled || props.isCopyDisabled"
      @applyCaption="handleApplyAICaption"
      @close="isAICaptionEditorVisible = false"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";

import LinkWithIcon from "@components/LinkWithIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import AICaptionEditor from "./AICaptionEditor.vue";
import CaptionEmojiPicker from "./CaptionEmojiPicker.vue";

import { MAX_CAPTION_LENGTH } from "@/constants/social";
import { getCalendarId } from "@logic/calendarManager";

const props = withDefaults(
  defineProps<{
    customerId: string;
    calendarId?: string;
    postId?: string;
    assetIds?: string[];
    isDisabled?: boolean;
    hasError?: boolean;
    placeholder?: string;
    textareaMinHeight?: string;
    aiEditorKey?: string;
    isCopyDisabled?: boolean;
    aiLinkColor?: "light-gray" | "gray" | "teal" | "blue" | "red" | "orange" | "green" | "yellow";
  }>(),
  {
    calendarId: "",
    postId: "",
    assetIds: () => [],
    isDisabled: false,
    hasError: false,
    placeholder: "",
    textareaMinHeight: "115px",
    aiEditorKey: undefined,
    isCopyDisabled: false,
    aiLinkColor: "gray",
  }
);

const emit = defineEmits<{
  (e: "focus"): void;
}>();

const caption = defineModel<string>("caption", { default: "" });

const captionTextareaRef = ref<HTMLTextAreaElement | null>(null);
const isAICaptionEditorVisible = ref<boolean>(false);
const isShowCopied = ref<boolean>(false);
const resolvedCalendarId = ref<string>(props.calendarId ?? "");
const isResolvingCalendarId = ref<boolean>(false);

watch(
  () => props.calendarId,
  calendarId => {
    if (calendarId) resolvedCalendarId.value = calendarId;
  }
);

const isCaptionAtLimit = computed(() => (caption.value ?? "").length >= MAX_CAPTION_LENGTH);

async function openAICaptionEditor(): Promise<void> {
  if (props.isDisabled || isResolvingCalendarId.value) return;

  if (!resolvedCalendarId.value && props.customerId) {
    isResolvingCalendarId.value = true;
    try {
      resolvedCalendarId.value = await getCalendarId(props.customerId);
    } finally {
      isResolvingCalendarId.value = false;
    }
  }

  isAICaptionEditorVisible.value = true;
}

function handleApplyAICaption(nextCaption: string): void {
  caption.value = nextCaption;
}

function insertEmojiAtCursor(emoji: string): void {
  const textarea = captionTextareaRef.value;
  const current = caption.value ?? "";
  if (textarea) {
    const start = textarea.selectionStart ?? current.length;
    const end = textarea.selectionEnd ?? current.length;
    const newText = current.slice(0, start) + emoji + current.slice(end);
    if (newText.length > MAX_CAPTION_LENGTH) return;
    caption.value = newText;
    const cursorPos = start + emoji.length;
    nextTick(() => {
      textarea.focus();
      textarea.setSelectionRange(cursorPos, cursorPos);
    });
    return;
  }
  const newText = current + emoji;
  if (newText.length > MAX_CAPTION_LENGTH) return;
  caption.value = newText;
}

function copyCaption(): void {
  const text = caption.value ?? "";
  navigator.clipboard.writeText(text);
  isShowCopied.value = true;
  setTimeout(() => (isShowCopied.value = false), 3000);
}
</script>

<style scoped lang="scss">
.post-caption-editor-panel {
  &__textarea {
    margin-bottom: 10px;
  }

  &__buttons-panel {
    gap: 10px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }

  &__copy-link {
    margin-left: auto;
  }

  &__ai-editor {
    margin-top: 12px;
  }

  &__char-count {
    text-align: right;
    color: rgba(0, 0, 0, 0.55);
    font-size: 13px;
  }

  &__limit-warning {
    color: #9a6600;
    font-size: 13px;
    font-weight: 600;
  }
}
</style>
